import { Fragment, type ReactNode } from 'react';

// These regexes handle the "Command: " or "Text: " prefix format
// Support both straight quotes (") and curly quotes (" ")
const PREFIX_REGEX = /^(?:Command|Text):\s*[""]/;
const SUFFIX_REGEX = /["""]$/;
const BOLD_REGEX = /(\*\*(?:.+?)\*\*)/g;
const IS_BOLD_REGEX = /^\*\*(?:.+?)\*\*$/;

function parseLocationString(location: unknown) {
  if (typeof location !== 'string' || !location) {
    return { prefix: '', suffix: '', lines: [] };
  }
  const prefixMatch = location.match(PREFIX_REGEX);
  /*
    The trailing quote is only framing when the `Command: "…"` opening half is
    present. Matching it unconditionally amputated the closing quote from any
    other location ending in one and re-emitted it as its own line, so a
    hidden-character `Message: "…"` read as a truncated payload (KIR-4229).
  */
  const suffixMatch = prefixMatch ? location.match(SUFFIX_REGEX) : null;
  let prefix = '';
  let suffix = '';
  let codeBlock = location;
  if (prefixMatch) {
    prefix = prefixMatch[0];
    codeBlock = codeBlock.slice(prefix.length);
  }
  if (suffixMatch) {
    suffix = suffixMatch[0];
    codeBlock = codeBlock.slice(0, -suffix.length);
  }
  const lines = codeBlock.split(/\r?\n/).filter(line => line.trim() !== '');
  return { prefix, suffix, lines };
}

export const ScanDetailFormattedText = ({
  location,
}: {
  location: string;
}): ReactNode => {
  const { prefix, suffix, lines } = parseLocationString(location);
  const isBold = (part: string) => IS_BOLD_REGEX.test(part);

  return (
    <>
      {prefix && (
        <div className="font-mono text-xs text-muted-foreground">{prefix}</div>
      )}
      {lines.map((line, lineIdx) => (
        <div
          key={lineIdx}
          className="font-mono bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded text-xs whitespace-pre-wrap break-all"
        >
          {line.split(BOLD_REGEX).map((part, idx) =>
            isBold(part) ? (
              <span key={idx} className="font-bold text-red-400">
                {part.slice(2, -2)}
              </span>
            ) : (
              <Fragment key={idx}>{part}</Fragment>
            )
          )}
        </div>
      ))}
      {suffix && (
        <div className="font-mono text-xs text-muted-foreground">{suffix}</div>
      )}
    </>
  );
};
